import sideBarCountryRiskMock from "@/src/__mocks__/api/SideBarMenuCountryRiskMock"
import sideBarMenuItemsMock from "@/src/__mocks__/api/SideBarMenuItemsMock"
import sideBarMenuReportCategoryMock from "@/src/__mocks__/api/SideBarMenuReportCategoryMock"
import { Link } from "@/src/common/components/Link"
import { SidebarMenuItem } from "@/src/sb/business-layer/types"
import { useAnalytics } from "@/src/sb/components/Analytics"
import AngleDown from "@/src/sb/components/icon/AngleDown"
import X from "@/src/sb/components/icon/X"
import { StearsLogoNode } from "@/src/sb/components/layouts/DefaultLayoutV3"
import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import classNames from "classnames"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

type SidebarProps = {
  onClose?: () => void
  asideFooter?: null
  sidebarMenuItems: Array<SidebarMenuItem>
  sideBarRootItemsToHide: Array<number>
}

const Sidebar = ({
  onClose,
  asideFooter = null,
  sidebarMenuItems,
  sideBarRootItemsToHide
}: SidebarProps) => {
  const analytics = useAnalytics()
  const [isMobile] = useWindowSize()
  const router = useRouter()
  const [sideBarOpenState, setSideBarOpenState] = useState<
    Record<string, boolean>
  >({})

  function buildTreeWithDepth(
    items: SidebarMenuItem[],
    maxDepth = 4,
    currentDepth = 1
  ): Array<SidebarMenuItem> {
    const tree: Array<SidebarMenuItem> = []

    items.forEach((item) => {
      if (item.parent_id === null && currentDepth === 1) {
        tree.push({ ...item, children: [] })
        buildChildren(item.id, tree[tree.length - 1], currentDepth + 1)
      }
    })

    function buildChildren(
      parentId: number,
      parentNode: SidebarMenuItem,
      depth: number
    ): void {
      if (depth > maxDepth) {
        return
      }

      items.forEach((item) => {
        if (item.parent_id === parentId) {
          const childNode: SidebarMenuItem = { ...item, children: [] }
          parentNode.children?.push(childNode)
          buildChildren(item.id, childNode, depth + 1)
        }
      })
    }

    return tree
  }
  const sideBarSubMenuTree: SidebarMenuItem[] = []

  sideBarSubMenuTree.push(
    ...buildTreeWithDepth(
      sideBarCountryRiskMock()
        .concat(sideBarMenuItemsMock())
        .concat(sideBarMenuReportCategoryMock())
        .concat(
          (sidebarMenuItems as Array<SidebarMenuItem>)
            ?.filter((navItem) => !!navItem.category_name)
            .map((navItem) =>
              navItem.parent_id
                ? navItem
                : {
                    ...navItem,
                    parent_id: sideBarMenuReportCategoryMock().find(
                      (category: SidebarMenuItem) =>
                        category.label.toLowerCase() ===
                        navItem.category_name?.toLowerCase()
                    )?.id
                  }
            )
        )
    )
  )

  useEffect(() => {
    const buildFlatIDLabelMap = (menuItems: Array<SidebarMenuItem>) =>
      menuItems.reduce((flatMap, menuItem): Record<string, boolean> => {
        if (!menuItem.children?.length) return flatMap

        return {
          ...flatMap,
          [menuItem.id + "-" + menuItem.label]: false,
          ...buildFlatIDLabelMap(menuItem.children)
        }
      }, {})

    const flatIDLabelMap = buildFlatIDLabelMap(sideBarSubMenuTree)
    setSideBarOpenState(flatIDLabelMap)
  }, [])

  const renderSideBarMenuItemChildren = (
    sideBarMenuItems: Array<SidebarMenuItem> | undefined,
    depth = 1
  ) => {
    return sideBarMenuItems?.length
      ? sideBarMenuItems.map((sideBarMenuItemChild: SidebarMenuItem) => {
          const hasChildren = sideBarMenuItemChild.children?.length

          const menuItemGeneratedKey: string =
            sideBarMenuItemChild.id + "-" + sideBarMenuItemChild.label

          const item = (
            <>
              {sideBarMenuItemChild.label}{" "}
              {hasChildren ? (
                <span
                  className={classNames(
                    "inline-block shrink-0",
                    sideBarOpenState[menuItemGeneratedKey]
                      ? "rotate-180"
                      : "-rotate-90"
                  )}
                >
                  <AngleDown />
                </span>
              ) : null}
            </>
          )
          const itemProps = {
            className: classNames(
              "pr-5 cursor-pointer flex items-center gap-4 justify-between transition-all",
              router.asPath === sideBarMenuItemChild.destination
                ? "text-gray-900 dark:text-stears_white"
                : "text-gray-700 dark:text-gray-400",
              {
                "text-sm leading-[21px] font-medium pl-5 h-8": depth === 1,
                "text-xs leading-[16px] py-[6px] pl-8 min-h-7": depth === 2,
                "text-xs leading-[16px] py-[4px] pl-12 min-h-6": depth >= 3
              }
            )
          }

          return (
            <section key={menuItemGeneratedKey}>
              {hasChildren ? (
                <section
                  {...itemProps}
                  onClick={() =>
                    setSideBarOpenState({
                      ...sideBarOpenState,
                      [menuItemGeneratedKey]:
                        !sideBarOpenState[menuItemGeneratedKey]
                    })
                  }
                >
                  {item}
                </section>
              ) : (
                <Link
                  {...itemProps}
                  href={sideBarMenuItemChild.destination}
                  onClick={() =>
                    analytics?.clickNavItem("menu_item", {
                      label: sideBarMenuItemChild.label,
                      destination: sideBarMenuItemChild.destination,
                      category_name: sideBarMenuItemChild.category_name ?? "",
                      context: "sidebar"
                    })
                  }
                >
                  {item}
                </Link>
              )}

              {sideBarOpenState[menuItemGeneratedKey]
                ? renderSideBarMenuItemChildren(
                    sideBarMenuItemChild.children,
                    depth + 1
                  )
                : null}
            </section>
          )
        })
      : null
  }

  return (
    <>
      <section
        className={
          "flex gap-4 justify-between items-center p-5 border-b-[0.5px] dark:border-gray-600 shrink-0 h-[60px] md:h-[80px] sticky md:static top-0 left-0 right-0 z-20 leading-[20px]"
        }
      >
        <StearsLogoNode useSVG height={21.3} />

        <button
          onClick={onClose}
          className={
            "flex justify-center items-center dark:bg-gray-600 w-6 h-6 md:hidden ml-auto"
          }
        >
          <X size={20} />
        </button>
      </section>

      <section className="h-[calc(100vh-60px)] md:h-[calc(100vh-80px)] flex flex-col relative">
        <section className={"grow flex flex-col overflow-auto no-scrollbar"}>
          {sideBarSubMenuTree
            .filter((sideBarMenuItem) => {
              if (!isMobile) return true
              return !sideBarRootItemsToHide.includes(sideBarMenuItem.id)
            })
            .map((sideBarMenuItem) => {
              const item = sideBarMenuItem.label
              const itemProps = {
                className: classNames(
                  "pl-4 flex gap-2 items-center text-gray-700 dark:text-gray-400 border-l-4 border-l-transparent transition-all sticky top-0 z-20 bg-stears_white dark:bg-[#0E0F10] leading-[16px]",
                  "pt-[10px] text-[10px] leading-[20px] h-8"
                )
              }

              return (
                <section
                  key={sideBarMenuItem.id + sideBarMenuItem.label}
                  className={classNames(
                    "border-b-[0.5px] dark:border-gray-600 last-of-type:border-none font-jakarta",
                    "pb-5"
                  )}
                >
                  {sideBarMenuItem.children?.length ? (
                    <section {...itemProps}>{item}</section>
                  ) : (
                    <Link
                      {...itemProps}
                      href={sideBarMenuItem.destination}
                      onClick={() =>
                        analytics?.clickNavItem("menu_item", {
                          label: sideBarMenuItem.label,
                          destination: sideBarMenuItem.destination,
                          category_name: sideBarMenuItem.category_name ?? "",
                          context: "sidebar"
                        })
                      }
                    >
                      {item}
                    </Link>
                  )}

                  <section>
                    {renderSideBarMenuItemChildren(sideBarMenuItem.children)}
                  </section>
                </section>
              )
            })}
        </section>

        <section className={"sticky bottom-0 px-5"}>{asideFooter}</section>
      </section>
    </>
  )
}

export default Sidebar
